import React, { useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/esm/Page/TextLayer.css";
import "react-pdf/dist/esm/Page/AnnotationLayer.css";
import { useTranslationContext } from "../../contexts/TranslationContext";
import "./Preview.css";
import { toast } from 'react-toastify';
import download from "../../assets/images/downloadIcon.svg"
import prevIcon from "../../assets/images/prevIcon.svg"
import nextIcon from "../../assets/images/nextIcon.svg"
import zoomout from "../../assets/images/zoomOut.svg"
import zoomIn from "../../assets/images/zoomIn.svg"
import close from "../../assets/images/previewClose.svg"
import { downloadFile } from "../../utility/downloadFile";

const PdfPreview = ({ pdfUrlFile, isOpen, onClose, isSummary, fileName}) => {
  pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

  const [pdfUrl, setPdfUrl] = useState(pdfUrlFile);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const {translations} = useTranslationContext();
  const [loadFailed, setLoadFailed] = useState(false);
  const pdfLoadError = translations["pdf_load_error"] || "Failed to load PDF. Please try again."
  const pdfOfflineError = translations["media_load_error"] || "You're offline. Please connect to the internet to load the PDF.";
  const downloadText = translations["download_text"] || "Download";
  const zoomOutText = translations["zoom_out_text"] || "Zoom Out";
  const zoomInText = translations["zoom_in_text"] || "Zoom In";
  const closeText = translations["close_text"] || "Close";
  const modalRef = useRef(null);

  useEffect(() => {
    if (isOpen && loadFailed) {
      setLoading(true);
      setPdfUrl(null);
      setTimeout(() => {
        setPdfUrl(pdfUrlFile);
        setLoadFailed(false);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    const handleOnline = () => {
      if (!loadFailed) return;
      setLoading(true);
      setPdfUrl(null);
      setTimeout(() => {
        setPdfUrl(pdfUrlFile);
        setLoadFailed(false);
      }, 300);
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [pdfUrlFile, loadFailed]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose(); // Close the modal
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen, onClose]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleDownload = async () => {
    try {
      await downloadFile({ url: pdfUrl, filename: "document.pdf" });
    } catch {
     toast.error("Download failed. Please try again.");
    }
  };

  return (
    <div className={`pdf-modal-overlay ${isOpen ? 'open' : ''}`}>
      <div className="pdf-modal" ref={modalRef}>
        <div className="pdf-modal-header">
          <div className="preview-title">
            {isSummary ? 'Document Summary' : fileName}
          </div>
          <div className="pdf-header-buttons">
            <button onClick={handleZoomIn} title={zoomInText} className="icon-button"><img src={zoomIn} alt="zoom-in"></img></button>
            <button onClick={handleZoomOut} disabled={scale <= 0.5} title={zoomOutText} className="icon-button"><img src={zoomout} alt="zoom-out" /></button>
            <button className="download-button" onClick={handleDownload}>
              <img src={download} alt="download-icon" />
              {downloadText}</button>
            <button className="close-button" onClick={onClose} title={closeText} ><img src={close} alt="zoom-in" /></button>
          </div>
        </div>

        {/* Scrollable PDF content */}
        <div className="pdf-scrollable-wrapper">
          <div className="pdf-viewer-container-preview">
            {pdfUrl ? (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={(error) => {
                console.error("PDF load error:", error);
                const isOffline = !navigator.onLine;
                if (isOffline) {
                  toast.error(pdfOfflineError);
                } else {
                  toast.error(pdfLoadError);
                }
                setPdfUrl(null);
                setLoadFailed(true);
                setLoading(false);
              }}
              >
                <Page pageNumber={pageNumber} scale={scale} />
              </Document>
            ) : (
              <p>No PDF to display</p>
            )}
          </div>
        </div>

        {/* Fixed footer */}
        {pdfUrl && numPages && (
          <div className="page-number-footer">
            <div className="page-navigation-footer">
              <button
                className="nav-arrow"
                onClick={() => setPageNumber(prev => Math.max(prev - 1, 1))}
                disabled={pageNumber === 1}
                title="Previous page"
              >
                <img src={prevIcon} alt="prev" />
              </button>

              <div className="page-number-display">{pageNumber}</div>

              <button
                className="nav-arrow"
                onClick={() => setPageNumber(prev => Math.min(prev + 1, numPages))}
                disabled={pageNumber === numPages}
                title="Next page"
              >
                <img src={nextIcon} alt="next" />
              </button>
            </div>
          </div>
        )}

        {loading && (
          <div className="pdf-loader">
            <div className="spinner-loader"></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PdfPreview;
